/**
 * vpclipRegistry — VPCLIP-INTERACTIVE (MASTERPLAN §7.1 / Р16.5 W2.6).
 *
 * VPCLIP-COMMAND (#496) shipped the scripted/CLI-only entry point
 * (`VPCLIP <vpId> <x,y> <x,y>` → `geometry/viewport-clip-set`, parsed by
 * `dynamicInput/vpclipCommand.ts`) plus the scissor-clip render arm. This
 * module is the MISSING interactive counterpart: running bare `VPCLIP`
 * (or its RU alias `ВЭКРЕЗ`) from the command line now opens a real
 * AutoCAD-parity pick session —
 *
 *   1. "Select viewport to clip:" — click a viewport border in the active
 *      paper-space layout (`vpclip-select-viewport`). A single
 *      pre-selected viewport skips this step (see
 *      `draftingHelpers.ts`'s VPCLIP pre-arm + `useDraftSession.ts`'s
 *      `vpclipViewportId` seed).
 *   2. "Specify first corner point:" — the first clip-rectangle corner
 *      (`vpclip-first-corner`).
 *   3. "Specify opposite corner point:" — the diagonal corner
 *      (`vpclip-second-corner`), which commits `geometry/viewport-clip-set`
 *      against the SELECTED viewport — the exact same typed command the
 *      CLI form dispatches, so history / undo / audit / AI / DXF export
 *      are unaffected (ONE producer, ONE truth — CLAUDE.md PRIORITY #2.1).
 *
 * Scope (per MASTERPLAN's VPCLIP-INTERACTIVE row, §5.3): rectangular pick
 * only. Polygonal interactive pick and a Delete-boundary affordance are
 * explicitly deferred to EPIC-VIEWPORT-CLIP's persisted clip-boundary
 * producer slice (tracked separately as N7 VPCLIP-BOUNDARY-EXPORT) — NOT
 * built here.
 *
 * Registered into the shared `ADVANCE_WITH_POINT` / `ADVANCE_WITH_TEXT`
 * tables in `advanceDispatch.ts` via `VPCLIP_POINT_ENTRIES` /
 * `VPCLIP_TEXT_ENTRIES` — mirrors `dimensionRegistry.ts` /
 * `blockAuthoringRegistry.ts`'s wrapper-module shape so `advanceDispatch.ts`
 * (already at the project's 2000-line file-size ceiling per CLAUDE.md
 * "Контроль размера файлов") gains only the import + spread, not the full
 * handler bodies.
 */

import type {
  ViewportEntity,
  DrawingEntity,
} from "@dxf-ai-editor/drawing-model";

import { t } from "../../i18n";
import { pointInViewport } from "../../scene3d/viewports/viewportTransform2d";
import { excludeControlViewport } from "../../scene3d/viewports/viewportControlFilter";
import type {
  AdvanceWithPointFn,
  AdvanceWithTextFn,
  DraftPoint,
  DraftStep,
} from "../draftTypes";

/**
 * Hit-test a paper-space click against every ACTIVE (`viewportStatus > 0`,
 * mirrors `Scene3DStage`'s own render-visibility gate) Viewport entity in
 * the active space, returning the FIRST match — mirrors
 * `viewportContextController.ts`'s own `.find(...)` double-click-to-enter-VP
 * hit-test (first/topmost wins, no special tie-break; one truth, CLAUDE.md
 * PRIORITY #2). The control viewport (paper space itself) is excluded via
 * the SAME `excludeControlViewport` rule `Scene3DStage`'s
 * `activeLayoutViewports` uses (@codex round-1 P1 — delegating to the
 * shared helper, rather than an inline `viewportId === 1` check here, is
 * what keeps this hit-test and the renderer's own control-VP exclusion
 * from silently drifting apart; see `viewportControlFilter.ts`'s doc
 * comment, including its round-10→11 history, for the exclusion rule's
 * exact scope).
 *
 * Coordinate frame (@codex round-1 P1, reaffirmed round-7 P2 after a
 * round-6 detour; frame flipped by DRAFT-DOCUMENT-FRAME): `point` is a
 * DOCUMENT (AutoCAD/DXF) coordinate. `clientToWorld` unprojects against the
 * OFFSET-world camera, but `pickLoop/interactivePickLoop.ts` converts back
 * at the `onCanvasPoint` emit through `ActiveInputSpace.toDocument`, so the
 * whole draft state machine works in document coordinates.
 * `ctx.paperOrigin` arrives in the SAME frame —
 * `useDraftSession.resolveDraftPaperOriginDocument` returns the raw
 * `LayoutPageSetup.origin`, deliberately NOT the `applyOffsetToPaperOrigin`
 * image of it that `Scene3DStage`'s `paperOriginOffset` hands the renderer.
 * Both sides in one frame is the precondition every `pointInViewport` call
 * depends on; mixing them misses every viewport by the whole session origin.
 * It must be compared against
 * `vp.center + paperOrigin` — NOT bare `vp.center` —
 * because `ViewportFrameLayer.tsx` (the renderer) positions the ACTUAL
 * on-screen viewport frame mesh at exactly that sum (`cx = center.x +
 * paperOrigin.x`, confirmed directly in its source; its own doc comment:
 * "`paperOrigin` translates the viewport rectangle into the sheet's world
 * frame so it aligns with the paper plane"). `pointInViewport`'s own
 * internal `vp.center + paperOrigin` formula matches this exactly, so
 * `ctx.paperOrigin` is threaded straight through.
 *
 * (Round-6 note for future readers: a round-6 pass temporarily removed
 * this origin term after a same-camera projection/unprojection round-trip
 * test appeared to show it was unnecessary. That test was a tautology —
 * `unproject(project(p)) === p` holds for ANY `p` on the SAME camera,
 * regardless of whether `p` corresponds to where anything actually
 * renders — so it could never have distinguished correct from incorrect
 * origin handling. The test scenario's OWN click-target math had the
 * matching bug (also omitting `+paperOrigin`), which is why the round-trip
 * "matched": both sides were consistently wrong in the same direction.
 * `ViewportFrameLayer.tsx`'s source and the resulting margin geometry
 * (confirmed against `tests-fixtures/multi-layout-simple.dxf`: a 277-wide
 * model-window viewport centers with an exact symmetric 10-unit margin
 * inside the 297-wide sheet ONLY with `+paperOrigin` applied) is the
 * ground truth, not a projection round-trip. Round-7 restored this file
 * to its round-1..5 state and fixed the scenario's click-target math
 * instead — see `docs/ui-reference/vpclip-interactive/scenario.json`.)
 *
 * Known limitation, shared with `viewportContextController.ts` (@codex
 * round-8 P2): when two viewport RECTANGLES overlap (e.g. a smaller VP
 * fully nested inside a larger one), "first match in `candidates`" is not
 * guaranteed to equal "topmost by paint order" — neither this hit-test
 * NOR `viewportContextController.ts`'s own identical `.find(...)`
 * double-click-to-enter-VP hit-test carries any z-order/paint-order
 * signal today (`activeLayoutViewports`/`geometryEntities` order is
 * import/creation order, not a tracked draw-order field), so a click
 * inside a nested VP's border can resolve the larger, further-back VP
 * instead. Deliberately NOT special-cased here: doing so would make this
 * ONE hit-test disagree with `viewportContextController.ts`'s sibling
 * hit-test on the SAME click (violating CLAUDE.md PRIORITY #2's "one
 * truth" for picking) — the correct fix is a shared z-order/paint-order
 * concept (or routing an ambiguous multi-candidate pick through the
 * existing `SelectionCyclingDialog` overlap-disambiguation mechanism,
 * mirroring how a regular entity pick already handles 2+ overlapping
 * candidates) applied to BOTH hit-tests together, which is out of scope
 * for VPCLIP-INTERACTIVE's rectangular-pick slice.
 */
function findViewportAtPaperPoint(
  point: DraftPoint,
  geometryEntities: ReadonlyArray<DrawingEntity>,
  paperOrigin: { x: number; y: number } | null,
): ViewportEntity | null {
  const origin = paperOrigin ?? { x: 0, y: 0 };
  const activeViewports = geometryEntities.filter(
    (entity): entity is ViewportEntity =>
      entity.type === "Viewport" && entity.viewportStatus > 0,
  );
  const candidates = excludeControlViewport(activeViewports);
  for (const vp of candidates) {
    if (pointInViewport(point, vp, origin)) return vp;
  }
  return null;
}

/**
 * Step 1 — "Select viewport to clip:". A click that misses every viewport
 * (or hits the control viewport) re-prompts on the SAME step, mirroring
 * OFFSET's `offset-select-entity` miss handling
 * (`draftModifyStateMachine.ts`) — AutoCAD never errors on a missed pick at
 * a select prompt, it just asks again.
 */
const vpclipSelectViewportPoint: AdvanceWithPointFn = (session, point, ctx) => {
  const vp = findViewportAtPaperPoint(
    point,
    ctx.geometryEntities,
    ctx.paperOrigin ?? null,
  );
  if (!vp) return { nextSession: session };
  return {
    nextSession: {
      ...session,
      step: "vpclip-first-corner",
      options: { ...session.options, vpclipViewportId: vp.id },
      hoverPoint: null,
    },
  };
};

/**
 * Step 1 text arm — Enter with no viewport picked cancels the command
 * (AutoCAD parity: Enter at a bare "Select objects"/"Select viewport"
 * prompt with nothing selected exits). Mirrors OFFSET's
 * `offset-select-entity` Enter-exit (`draftModifyStateMachine.ts:1637`).
 * Any OTHER typed text at this select-only step is a no-op re-prompt — the
 * fully-typed scripted form (`VPCLIP <vpId> ...`) is a SEPARATE, earlier
 * pipeline stage (`dynamicInput/vpclipCommand.ts`, short-circuited before a
 * draft session ever starts), so a stray token here is never a valid
 * viewport handle in this step's own context.
 */
const vpclipSelectViewportText: AdvanceWithTextFn = (session, ctx) => {
  if (ctx.text === "") return { nextSession: null };
  return { nextSession: session };
};

/**
 * Step 2 — "Specify first corner point:". Stores the first clip-rectangle
 * corner and advances; mirrors `viewport-first-corner` (MVIEW) exactly.
 */
const vpclipFirstCornerPoint: AdvanceWithPointFn = (session, point) => ({
  nextSession: {
    ...session,
    step: "vpclip-second-corner",
    points: [point],
    hoverPoint: null,
  },
});

/**
 * Step 3 — "Specify opposite corner point:". Commits the SAME typed
 * `geometry/viewport-clip-set` the CLI form's `VPCLIP <vp> x,y x,y` emits
 * (`dynamicInput/vpclipCommand.ts`) — one producer, no parallel mutation
 * path (CLAUDE.md PRIORITY #2.1). `applyViewportClipSet`
 * (`packages/command-engine/src/viewportCommands.ts`) owns the
 * center/width/height reshape + prior-clip invalidation; this step only
 * supplies the two picked paper-space corners.
 *
 * Coordinate frame (@codex round-1 P1, reaffirmed round-7 P1 after a
 * round-6 detour — see `findViewportAtPaperPoint`'s doc comment for the
 * full story): the picked `point`/`firstCorner` arrive in PAPER-WORLD
 * coordinates — `ctx.paperOrigin` already baked in, since that is what
 * `vpclip-select-viewport`'s `pointInViewport(paperPt, vp, paperOrigin)`
 * hit test requires. `applyViewportClipSet.bounds` — like `vp.center`
 * itself and the CLI form's own `VPCLIP <vp> x,y x,y` (whose docstring
 * states its points are bare "PAPER-space (sheet) coordinates", i.e.
 * WITHOUT the origin) — is SHEET-LOCAL: `ViewportFrameLayer.tsx` re-adds
 * `paperOrigin` on top at render time. Committing the raw paper-world pick
 * would double-count the origin and jump the clipped viewport by that
 * offset on any layout with a non-zero `LayoutPageSetup.origin`, so both
 * corners are re-expressed in the sheet-local frame here before building
 * the command.
 *
 * WHY THERE IS STILL NO `unapplyOffset` HERE (DRAFT-DOCUMENT-FRAME):
 * `point` and `ctx.paperOrigin` are now both DOCUMENT values (see
 * `findViewportAtPaperPoint` above), and `bounds` is persisted on the
 * ViewportEntity and must be a document value, so the subtraction below is
 * simply sheet-local arithmetic in one frame and needs no conversion at all.
 * It needed none under the previous convention either, where both operands
 * were OFFSET-world, because a difference of two paper quantities is
 * invariant under the pure translation that separates the frames:
 *
 *     (pRaw − O) − (originRaw − O)  ===  pRaw − originRaw
 *
 * Either way the sheet-local value committed here is EXACTLY the one a user
 * would read in AutoCAD. Adding an `unapplyOffset` on top would re-introduce
 * `+O` into a persisted document field — the corruption this comment exists
 * to prevent. What is NOT optional is that both operands share ONE frame;
 * that is why `resolveDraftPaperOriginDocument` carries its frame in its
 * name. The invariance is locked by a round-trip property test
 * (`__tests__/paperOriginRenderFrame.metamorphic.test.ts`) and by
 * `hooks/useDraftSession.documentFrame.test.tsx`.
 */
const vpclipSecondCornerPoint: AdvanceWithPointFn = (session, point, ctx) => {
  const viewportId = session.options.vpclipViewportId;
  // Registry-completeness guard — unreachable via the real FSM (this step
  // is only entered via `vpclip-first-corner`, which is only entered via
  // `vpclip-select-viewport`'s hit or a pre-armed `vpclipViewportId`), but
  // a corrupted/replayed persisted session must no-op rather than dispatch
  // a targetless clip-set.
  if (!viewportId) return { nextSession: null };
  const firstCorner = session.points[0];
  if (!firstCorner) return { nextSession: null };
  const origin = ctx.paperOrigin ?? { x: 0, y: 0 };
  const sheetLocalFirst = {
    x: firstCorner.x - origin.x,
    y: firstCorner.y - origin.y,
  };
  const sheetLocalSecond = { x: point.x - origin.x, y: point.y - origin.y };
  return {
    nextSession: null,
    commit: {
      geometryCommand: {
        type: "geometry/viewport-clip-set",
        source: "scene",
        viewportId,
        bounds: {
          x1: sheetLocalFirst.x,
          y1: sheetLocalFirst.y,
          x2: sheetLocalSecond.x,
          y2: sheetLocalSecond.y,
        },
      },
      // Unused by the `geometryCommand` commit path today (`useDraftSession
      // .commitDraftedEntity` dispatches the command and returns without
      // reading `summary`/`commandText` — the visible feed message is the
      // reducer-level `audit.viewport_clip_set` from
      // `geometryShellViewportAudit.ts`, the SAME one the CLI form
      // produces). Populated anyway because `DraftCommitResult.summary` is
      // a required field, and a semantically-correct value is cheap
      // insurance against a future reader.
      summary: t("summary.viewport_clip_set", { id: viewportId }),
      commandText: "VPCLIP",
    },
  };
};

export const VPCLIP_POINT_ENTRIES: ReadonlyArray<
  readonly [DraftStep, AdvanceWithPointFn]
> = [
  ["vpclip-select-viewport", vpclipSelectViewportPoint],
  ["vpclip-first-corner", vpclipFirstCornerPoint],
  ["vpclip-second-corner", vpclipSecondCornerPoint],
];

export const VPCLIP_TEXT_ENTRIES: ReadonlyArray<
  readonly [DraftStep, AdvanceWithTextFn]
> = [["vpclip-select-viewport", vpclipSelectViewportText]];
